/**
 * Homepage Featured cards must show a real listener face — never the Sweet
 * Listeners lockup / mark, and never an empty photo slot.
 */
import { listingIdOf } from './featured';

export const HOMEPAGE_LISTENER_CARD_LIMIT = 3;
/** Extra published faces so we can drop a lockup and still show 3 cards. */
export const HOMEPAGE_LISTENER_CANDIDATE_LIMIT = 8;

const LISTING_VARIANT_KEYS = [
  'listing-card-2x',
  'listing-card',
  'square-small2x',
  'square-small',
  'square-xsmall2x',
  'square-xsmall',
  'scaled-medium',
  'scaled-small',
  'scaled-large',
];

const PROFILE_VARIANT_KEYS = [
  'square-small2x',
  'square-small',
  'square-xsmall2x',
  'square-xsmall',
  'scaled-small',
  'scaled-medium',
];

const BRAND_PHOTO_URL_HINTS = [
  '/static/branding/',
  'sweet-listeners-icon',
  'sweet-listeners-lockup',
  'sweet-listeners-original-logo',
  'sweet-listeners-mark',
  'sweet-listeners-ear-heart',
  'sweet-listeners-waitlist-lockup',
];

/**
 * Known TEST listing images that are the official lockup / waitlist mark,
 * not a face. Sharetribe CDN URLs do not include our /static/branding path.
 */
export const BRAND_MARK_IMAGE_IDS = [
  '6aaad71f-ae23-491a-87e5-6f14727be6c3', // Sam man — stacked lockup
  '6aaad411-9168-4bee-87c5-f1ce06c4eecc', // Sam Jackson — recruiting lockup
  '6aaad54a-9f78-4690-8369-916b8c0cf607', // Sam Jackson — recruiting lockup variant
];

const firstVariantUrl = (variants, keys) => {
  if (!variants) {
    return null;
  }
  const preferred = (keys || []).map(key => variants[key]?.url).find(Boolean);
  if (preferred) {
    return preferred;
  }
  const first = Object.values(variants).find(variant => variant?.url);
  return first?.url || null;
};

const imageIdOf = image => {
  if (!image) {
    return null;
  }
  if (typeof image.id === 'string') {
    return image.id;
  }
  return image.id?.uuid || null;
};

/**
 * True when this URL / image id is the brand mark or otherwise not a face.
 *
 * @param {string|null} url
 * @param {string|null} [imageId]
 * @returns {boolean}
 */
export const isBrandMarkPhoto = (url, imageId) => {
  if (!url || typeof url !== 'string') {
    return true;
  }
  const lower = url.toLowerCase();
  if (BRAND_PHOTO_URL_HINTS.some(hint => lower.includes(hint))) {
    return true;
  }
  if (BRAND_MARK_IMAGE_IDS.some(id => lower.includes(id))) {
    return true;
  }
  if (imageId && BRAND_MARK_IMAGE_IDS.includes(imageId)) {
    return true;
  }
  return false;
};

/**
 * First usable URL on a listing or profile image entity.
 *
 * @param {Object} [image]
 * @returns {string|null}
 */
export const imageEntityUrl = image => {
  const variants = image?.attributes?.variants;
  return (
    firstVariantUrl(variants, LISTING_VARIANT_KEYS) ||
    firstVariantUrl(variants, PROFILE_VARIANT_KEYS) ||
    (typeof image?.attributes?.url === 'string' ? image.attributes.url : null)
  );
};

/**
 * First listing image that is a real face, not the brand lockup.
 *
 * @param {Object} [listing]
 * @returns {Object|null}
 */
export const firstUsableListingImage = listing => {
  const images = listing?.images || [];
  for (let i = 0; i < images.length; i += 1) {
    const image = images[i];
    const url = imageEntityUrl(image);
    if (url && !isBrandMarkPhoto(url, imageIdOf(image))) {
      return image;
    }
  }
  return null;
};

/**
 * Header / menu avatar image. Profile photo first, then the listing face.
 *
 * @param {Object} [params]
 * @param {Object} [params.user]
 * @param {Object} [params.listing]
 * @returns {Object|null}
 */
export const navAvatarImage = ({ user, listing } = {}) => {
  const profile = user?.profileImage;
  const profileUrl = imageEntityUrl(profile);
  if (profileUrl && !isBrandMarkPhoto(profileUrl, imageIdOf(profile))) {
    return profile;
  }
  return firstUsableListingImage(listing);
};

/**
 * Which photo the header / menu avatar is using.
 *
 * @param {Object} [params]
 * @param {Object} [params.user]
 * @param {Object} [params.listing]
 * @returns {'profile'|'listing'|'initials'}
 */
export const navAvatarSource = ({ user, listing } = {}) => {
  const profile = user?.profileImage;
  const profileUrl = imageEntityUrl(profile);
  if (profileUrl && !isBrandMarkPhoto(profileUrl, imageIdOf(profile))) {
    return 'profile';
  }
  if (firstUsableListingImage(listing)) {
    return 'listing';
  }
  return 'initials';
};

/**
 * Jitsi only accepts https avatar URLs. Relative / http paths stay omitted.
 *
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export const httpsAvatarURL = url =>
  typeof url === 'string' && /^https:\/\//i.test(url) ? url : null;

/**
 * Photo for this person's Jitsi tile. Profile image first. Listing face only
 * when this person owns the listing (listeners keep the photo on the listing).
 *
 * @param {Object} [params]
 * @param {Object} [params.user]
 * @param {Object} [params.listing]
 * @param {boolean} [params.useListingPhoto]
 * @returns {string|null}
 */
export const sessionSelfAvatarURL = ({ user, listing, useListingPhoto } = {}) => {
  const fromUser = httpsAvatarURL(imageEntityUrl(user?.profileImage));
  if (fromUser) {
    return fromUser;
  }
  if (useListingPhoto) {
    return httpsAvatarURL(listingCardPhotoUrl(listing));
  }
  return null;
};

/**
 * Favorites thumbnail: the hearted listing's own photo, even if Featured
 * would skip a lockup. Initials only when no image URL exists.
 *
 * @param {Object} [listing]
 * @returns {string|null}
 */
export const favoriteListingPhotoUrl = listing => {
  const images = listing?.images || [];
  for (let i = 0; i < images.length; i += 1) {
    const url = imageEntityUrl(images[i]);
    if (url) {
      return url;
    }
  }
  const profileImageUrl =
    imageEntityUrl(listing?.profileImage) || imageEntityUrl(listing?.author?.profileImage);
  if (profileImageUrl) {
    return profileImageUrl;
  }
  return listingCardPhotoUrl(listing);
};

/**
 * Listing photo first, then author profile photo. Skips brand-mark images.
 *
 * @param {Object} [listing]
 * @returns {string|null}
 */
export const listingCardPhotoUrl = listing => {
  const images = listing?.images || [];
  for (let i = 0; i < images.length; i += 1) {
    const image = images[i];
    const listingUrl = firstVariantUrl(image?.attributes?.variants, LISTING_VARIANT_KEYS);
    if (listingUrl && !isBrandMarkPhoto(listingUrl, imageIdOf(image))) {
      return listingUrl;
    }
  }

  const profileUrl = firstVariantUrl(
    listing?.author?.profileImage?.attributes?.variants,
    PROFILE_VARIANT_KEYS
  );
  const profileImgId = imageIdOf(listing?.author?.profileImage);
  if (profileUrl && !isBrandMarkPhoto(profileUrl, profileImgId)) {
    return profileUrl;
  }

  return null;
};

/**
 * @param {Object} [listing]
 * @returns {boolean}
 */
export const listingHasRealFacePhoto = listing => !!listingCardPhotoUrl(listing);

const isBrandMarkOnlyListing = listing => {
  if (listingHasRealFacePhoto(listing)) {
    return false;
  }
  const images = listing?.images || [];
  return images.some(image =>
    isBrandMarkPhoto(
      firstVariantUrl(image?.attributes?.variants, LISTING_VARIANT_KEYS),
      imageIdOf(image)
    )
  );
};

/**
 * Featured-with-photos first (caller order), then fill so a missing include
 * or lockup drop cannot silently shrink the strip to one card.
 *
 * @param {Array<Object>} [listings]
 * @param {number} [limit]
 * @returns {Array<Object>}
 */
export const pickHomepageListenerCards = (listings = [], limit = HOMEPAGE_LISTENER_CARD_LIMIT) => {
  const seen = new Set();
  const withFace = [];
  const withoutFace = [];
  const lockups = [];

  (listings || []).forEach(listing => {
    const id = listingIdOf(listing);
    if (!id || seen.has(id)) {
      return;
    }
    if (listing?.attributes?.deleted) {
      return;
    }
    if (listing?.attributes?.state && listing.attributes.state !== 'published') {
      return;
    }
    seen.add(id);
    if (listingHasRealFacePhoto(listing)) {
      withFace.push(listing);
    } else if (isBrandMarkOnlyListing(listing)) {
      lockups.push(listing);
    } else {
      withoutFace.push(listing);
    }
  });

  // Brand-mark-only cards stay off Featured. Everyone else is kept so a
  // 3-card slice cannot hide the rest of the published set.
  return [...withFace, ...withoutFace].slice(0, limit);
};
